import React from "react";
import {
  IconSunrise,
  IconSun,
  IconSunset2,
  IconMoon,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { DaytimeSlot } from "@/types/pillbox";
import styles from "./DaytimeSlotRow.module.css";

export interface DaytimeSlotRowProps {
  counts: Record<DaytimeSlot, number>;
  /** The daytime slot the current wall-clock time falls in — only pass
   *  this when the selected day is today; the highlighted border only
   *  makes sense for "right now". */
  activeSlot?: DaytimeSlot | null;
  /** Called when a slot card is tapped — scrolls to/highlights its section. */
  onSelect?: (period: DaytimeSlot) => void;
}

const SLOT_COLORS: Record<
  DaytimeSlot,
  { bg: string; label: string; count: string }
> = {
  morning: { bg: "#fcf7ec", label: "#7d6224", count: "#5f4b1b" },
  afternoon: { bg: "#fdf4ec", label: "#804d23", count: "#623b1b" },
  evening: { bg: "#f9ecf4", label: "#8b3166", count: "#6c264f" },
  night: { bg: "#edf3fc", label: "#3263a3", count: "#274c7e" },
};

const SLOTS: Array<{ key: DaytimeSlot; icon: React.ReactNode }> = [
  { key: "morning", icon: <IconSunrise size={24} stroke={1.5} /> },
  { key: "afternoon", icon: <IconSun size={24} stroke={1.5} /> },
  { key: "evening", icon: <IconSunset2 size={24} stroke={1.5} /> },
  { key: "night", icon: <IconMoon size={24} stroke={1.5} /> },
];

const DaytimeSlotRow: React.FC<DaytimeSlotRowProps> = ({
  counts,
  activeSlot,
  onSelect,
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.daytimeCard}>
      {SLOTS.map((slot) => {
        const colors = SLOT_COLORS[slot.key];
        const isActive = activeSlot === slot.key;
        return (
          <button
            type="button"
            className={`${styles.slot} ${isActive ? styles.active : ""}`}
            key={slot.key}
            style={{ background: colors.bg }}
            onClick={() => onSelect?.(slot.key)}
          >
            <span style={{ color: colors.count }}>{slot.icon}</span>
            <div className={styles.slotText}>
              <p className={styles.slotLabel} style={{ color: colors.label }}>
                {t(`pillbox.daytime.${slot.key}`)}
              </p>
              <p className={styles.slotCount} style={{ color: colors.count }}>
                {counts[slot.key]}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default DaytimeSlotRow;
